import PropTypes from "prop-types"
import React from "react"
import { useMarina } from "src/contexts/MarinaContext"
import InvoiceLink from "src/main/Billing/Links/InvoiceLink"

import Timeline from "src/components/Timeline"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"

import {
  INVOICE_ICON,
  PAYMENT_ICON,
  describeOfflineType,
  formatDay,
  formatMoment,
  methodLabel,
  methodWithReference,
} from "./helpers"

const KINDS = [
  "payment",
  "refund",
  "refund_failed",
  "payment_failed",
  "dispute",
]

// The feed has no badges, so a label carries any state no later row explains.
const LABELS = {
  payment: {
    failed: "Payment attempted",
    processing: "Payment processing",
    default: "Payment received",
  },
  refund: { pending: "Refund pending", default: "Refund" },
  refund_failed: { default: "Refund failed" },
  payment_failed: {
    canceled: "Canceled on the card reader",
    default: "Payment failed",
  },
  dispute: {
    won: "Dispute won",
    lost: "Dispute lost",
    warning_closed: "Inquiry closed",
    warning_needs_response: "Inquiry opened",
    warning_under_review: "Inquiry opened",
    default: "Dispute opened",
  },
}

const OPEN_DISPUTE_STATUSES = [
  "needs_response",
  "under_review",
  "warning_needs_response",
  "warning_under_review",
]

const KIND_ICONS = {
  payment: PAYMENT_ICON,
  refund: "fa-regular fa-arrow-turn-left",
  refund_failed: "fa-regular fa-circle-exclamation",
  payment_failed: "fa-regular fa-circle-exclamation",
  dispute: "fa-regular fa-scale-balanced",
}

const eventLabel = ({ kind, status }) =>
  LABELS[kind][status] || LABELS[kind].default

const methodFor = (event, payment) => {
  switch (event.kind) {
    case "payment":
      return methodWithReference(payment)
    case "refund":
      return event.refundPaymentType
        ? describeOfflineType({
            paymentType: event.refundPaymentType,
            checkNumber: event.refundCheckNumber,
            achConfirmation: event.refundAchConfirmation,
          })
        : methodLabel(payment)
    default:
      return null
  }
}

const detailFor = (event) => {
  if (event.kind === "payment_failed" && event.status === "canceled") {
    return "Nothing was collected"
  }
  if (event.kind !== "dispute") return event.reasonLabel

  return [
    OPEN_DISPUTE_STATUSES.includes(event.status) && event.statusLabel,
    event.reasonLabel && `Reason: ${event.reasonLabel}`,
  ]
    .filter(Boolean)
    .join(" · ")
}

const toItems = (timeline) =>
  timeline.flatMap((event) => [
    { id: event.id, icon: KIND_ICONS[event.kind], event },
    ...event.invoices.map((invoice) => ({
      id: `${event.id}-${invoice.invoiceId}`,
      icon: INVOICE_ICON,
      event,
      invoice,
    })),
  ])

const PaymentTimeline = ({ payment }) => {
  const { presentmentCurrency } = useMarina()
  const money = (amount) =>
    formatCurrencyFromMinorUnits({ amount, currency: presentmentCurrency })

  const columns = [
    {
      key: "event",
      header: "Event",
      minWidth: "240px",
      render: ({ event, invoice }) =>
        invoice ? (
          <div>
            <span className="block text-gray-900">
              Applied to{" "}
              {invoice.invoicePath ? (
                <InvoiceLink
                  invoiceId={invoice.invoiceId}
                  href={invoice.invoicePath}
                  className="font-semibold text-blue-600"
                >
                  {invoice.displayId}
                </InvoiceLink>
              ) : (
                <span className="font-semibold">{invoice.displayId}</span>
              )}
            </span>
            <span className="text-gray-700">
              <a
                href={invoice.sourcePath}
                className="font-semibold text-blue-600"
              >
                {invoice.kindLabel}
              </a>
              {` · Due ${formatDay(invoice.dueOn)}`}
            </span>
          </div>
        ) : (
          <div>
            <span className="block font-semibold text-gray-900">
              {eventLabel(event)}
            </span>
            {detailFor(event) && (
              <span className="block text-gray-700">{detailFor(event)}</span>
            )}
          </div>
        ),
    },
    {
      key: "date",
      header: "Date",
      width: "190px",
      render: ({ event }) => (
        <span className="whitespace-nowrap">
          {formatMoment(event.occurredAtLocal)}
        </span>
      ),
    },
    {
      key: "method",
      header: "Method",
      width: "180px",
      render: ({ event, invoice }) =>
        invoice ? null : methodFor(event, payment) || "—",
    },
    {
      key: "amount",
      header: "Amount",
      width: "120px",
      align: "right",
      render: ({ event, invoice }) =>
        invoice ? (
          <span className="text-gray-500">{money(invoice.amountCents)}</span>
        ) : (
          money(event.amountCents)
        ),
    },
    {
      key: "netReceived",
      header: "Net received",
      width: "130px",
      align: "right",
      render: ({ event, invoice }) =>
        invoice ? (
          <>
            <span className="text-gray-400" aria-hidden="true">
              —
            </span>
            <span className="sr-only">Not applicable</span>
          </>
        ) : (
          money(event.netReceivedCents)
        ),
    },
  ]

  return (
    <section aria-labelledby="payment-activity-heading">
      <Timeline
        label="Payment activity"
        heading={
          <h2
            id="payment-activity-heading"
            className="m-0 flex items-center gap-2 text-base font-semibold text-gray-900"
          >
            <i
              className="fa-kit fa-payment-activity text-gray-700"
              aria-hidden="true"
            />
            Payment activity
          </h2>
        }
        columns={columns}
        items={toItems(payment.timeline)}
      />
    </section>
  )
}

const invoiceShape = PropTypes.shape({
  invoiceId: PropTypes.string.isRequired,
  displayId: PropTypes.string.isRequired,
  invoicePath: PropTypes.string,
  dueOn: PropTypes.string.isRequired,
  kindLabel: PropTypes.string.isRequired,
  sourcePath: PropTypes.string.isRequired,
  amountCents: PropTypes.number.isRequired,
})

PaymentTimeline.propTypes = {
  payment: PropTypes.shape({
    paymentKind: PropTypes.string.isRequired,
    paymentMethodType: PropTypes.string,
    cardBrand: PropTypes.string,
    bankName: PropTypes.string,
    cardLast4: PropTypes.string,
    offlinePaymentType: PropTypes.string,
    checkNumber: PropTypes.string,
    achConfirmation: PropTypes.string,
    timeline: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        kind: PropTypes.oneOf(KINDS).isRequired,
        occurredAtLocal: PropTypes.string.isRequired,
        amountCents: PropTypes.number.isRequired,
        status: PropTypes.string,
        statusLabel: PropTypes.string,
        reasonLabel: PropTypes.string,
        refundPaymentType: PropTypes.string,
        refundCheckNumber: PropTypes.string,
        refundAchConfirmation: PropTypes.string,
        netReceivedCents: PropTypes.number.isRequired,
        invoices: PropTypes.arrayOf(invoiceShape).isRequired,
      })
    ).isRequired,
  }).isRequired,
}

export default PaymentTimeline
